import React from 'react';
import PropTypes from 'prop-types';

import { formInputTypes } from '@Components/configs';

import {
  ProductFormFieldContainer,
  ProductFormDropdownSelect,
  ProductFormTimezoneSelect,
  ProductFormCalendarInput,
  ProductFormSingleTextInput,
  ProductFormModalSelect,
  ProductFormMultipleTextInputs,
  ProductFormTextAreaInput,
  ProductFormMediaUpload,
  ProductFormMultipleTagInputs,
  ProductFormMultipleSuggestionInputs,
  ProductFormMultipleGroupInputs,
  ProductFormHybridInputSelect,
} from '@Components/Partial/Product/NewSearch';

const FormComponentSelector = ({
  fieldType,
  fieldTitle,
  description = null,
  inputConfig = null,
  searchTypeKey = null,
  inputKey,
  resetKey,
  options = null,
  value = null,
  handleFormValues,
  small,
  minStartDate = null,
  timeZone,
  setTimeZone,
  limit = null,
  charLimit = null,
  selectable = false,
  autoSelectSingleRadio = false,
  platformOptionOverrides = null,
  isBulkSearch = false,
  useCatalog = false,
  onUseCatalogChange = null,
  companyId = null,
  onUploadStart = null,
  onUploadEnd = null,
}) => {
  const {
    singleUrlInput,
    multipleUrlInput,
    singleTextInput,
    multipleTextInput,
    multiLinedTextInput,
    multipleGroupInput,
    textArea,
    datetimeEntry,
    multipleDatetimeEntry,
    timezoneSelect,
    radioSelect,
    multiSelect,
    tagsInput,
    suggestionsInput,
    singleMediaUpload,
    multipleMediaUpload,
    hybridInputSelect,
  } = formInputTypes;

  switch (fieldType) {
    case singleUrlInput:
    case singleTextInput:
      return (
        <ProductFormSingleTextInput
          key={resetKey}
          small={small}
          value={value || ''}
          handleFormValues={(val) => handleFormValues(val)}
        />
      );

    case multipleUrlInput:
    case multipleTextInput:
      return (
        <ProductFormMultipleTextInputs
          key={resetKey}
          inputConfig={inputConfig}
          searchTypeKey={searchTypeKey}
          small={small}
          value={value || []}
          handleFormValues={(val) => handleFormValues(val)}
          maxInputs={limit}
        />
      );

    case multipleGroupInput:
      return (
        <ProductFormMultipleGroupInputs
          key={resetKey}
          inputConfig={inputConfig}
          searchTypeKey={searchTypeKey}
          inputType="sets"
          small={small}
          value={value || ''}
          handleFormValues={(val) => handleFormValues(val)}
          maxInputs={limit}
          charLimit={charLimit}
        />
      );

    case multiLinedTextInput:
      return (
        <ProductFormMultipleGroupInputs
          key={resetKey}
          inputConfig={inputConfig}
          searchTypeKey={searchTypeKey}
          inputType="textArea"
          small={small}
          value={value || ''}
          handleFormValues={(val) => handleFormValues(val)}
          maxInputs={limit}
          charLimit={charLimit}
        />
      );

    case textArea:
      return (
        <ProductFormTextAreaInput
          key={resetKey}
          small={small}
          handleFormValues={(val) => handleFormValues(val)}
          limit={limit}
          charLimit={charLimit}
        />
      );

    case datetimeEntry:
      return (
        <ProductFormCalendarInput
          key={resetKey}
          inputConfig={inputConfig}
          searchTypeKey={searchTypeKey}
          small={small}
          value={value || undefined}
          handleFormValues={(val) => handleFormValues(val)}
          minStartDate={minStartDate}
          timeZone={timeZone}
          setTimeZone={setTimeZone}
        />
      );

    case multipleDatetimeEntry:
      return (
        <ProductFormMultipleGroupInputs
          key={resetKey}
          inputType="date"
          inputConfig={inputConfig}
          searchTypeKey={searchTypeKey}
          small={small}
          value={value || undefined}
          options={options}
          handleFormValues={(val) => handleFormValues(val)}
          maxInputs={limit}
          timeZone={timeZone}
          setTimeZone={setTimeZone}
        />
      );

    case timezoneSelect:
      return (
        <ProductFormTimezoneSelect
          key={resetKey}
          inputConfig={inputConfig}
          searchTypeKey={searchTypeKey}
          small={small}
          value={value || undefined}
          handleFormValues={(val) => handleFormValues(val)}
        />
      );

    case radioSelect:
      return (
        <ProductFormDropdownSelect
          key={resetKey}
          inputConfig={inputConfig}
          searchTypeKey={searchTypeKey}
          small={small}
          value={value}
          handleFormValues={(val) => handleFormValues(val)}
          simpleOptions={options}
          selectable={selectable}
          autoSelect={autoSelectSingleRadio}
        />
      );

    case multiSelect:
      return (
        <ProductFormModalSelect
          key={resetKey}
          inputConfig={inputConfig}
          searchTypeKey={searchTypeKey}
          small={small}
          options={options}
          value={value}
          handleFormValues={(val) => handleFormValues(val)}
          fieldTitle={fieldTitle}
          description={description}
          maxInputs={limit}
          platformOptionOverrides={inputKey === 'platform' && platformOptionOverrides}
        />
      );

    case tagsInput:
    case suggestionsInput:
      if (options && options.length > 0) {
        return (
          <ProductFormMultipleSuggestionInputs
            key={resetKey}
            inputConfig={inputConfig}
            searchTypeKey={searchTypeKey}
            small={small}
            handleFormValues={(val) => handleFormValues(val)}
            options={options}
          />
        );
      }

      return (
        <ProductFormMultipleTagInputs
          key={resetKey}
          inputConfig={inputConfig}
          searchTypeKey={searchTypeKey}
          small={small}
          handleFormValues={(val) => handleFormValues(val)}
        />
      );

    case singleMediaUpload:
      return (
        <ProductFormMediaUpload
          singleFile
          key={resetKey}
          inputConfig={inputConfig}
          searchTypeKey={searchTypeKey}
          small={small}
          maxInputs={1}
          handleFormValues={(val) => handleFormValues(val)}
          isBulkSearch={isBulkSearch}
          useCatalog={useCatalog}
          onUseCatalogChange={onUseCatalogChange}
        />
      );

    case multipleMediaUpload:
      return (
        <ProductFormMediaUpload
          key={resetKey}
          inputConfig={inputConfig}
          searchTypeKey={searchTypeKey}
          small={small}
          handleFormValues={(val) => handleFormValues(val)}
          maxInputs={limit}
          isBulkSearch={isBulkSearch}
          useCatalog={useCatalog}
          onUseCatalogChange={onUseCatalogChange}
          companyId={companyId}
          onUploadStart={onUploadStart}
          onUploadEnd={onUploadEnd}
        />
      );

    case hybridInputSelect:
      return (
        <ProductFormHybridInputSelect
          key={resetKey}
          inputConfig={inputConfig}
          searchTypeKey={searchTypeKey}
          options={options}
          small={small}
          value={value || ''}
          handleFormValues={(val) => handleFormValues(val)}
        />
      );

    default:
      return null;
  }
};

FormComponentSelector.propTypes = {
  fieldType: PropTypes.string.isRequired,
  fieldTitle: PropTypes.string.isRequired,
  searchTypeKey: PropTypes.string,
  inputKey: PropTypes.string.isRequired,
  resetKey: PropTypes.string.isRequired,
  description: PropTypes.string,
  options: PropTypes.arrayOf(PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.bool,
  ]).isRequired),
  value: PropTypes.any,
  inputConfig: PropTypes.objectOf(PropTypes.any),
  minStartDate: PropTypes.instanceOf(Date),
  timeZone: PropTypes.string.isRequired,
  setTimeZone: PropTypes.func.isRequired,
  limit: PropTypes.number,
  charLimit: PropTypes.number,
  selectable: PropTypes.bool,
  handleFormValues: PropTypes.func.isRequired,
  small: PropTypes.bool.isRequired,
  autoSelectSingleRadio: PropTypes.bool,
  platformOptionOverrides: PropTypes.arrayOf(PropTypes.string),
  isBulkSearch: PropTypes.bool,
  useCatalog: PropTypes.bool,
  onUseCatalogChange: PropTypes.func,
  companyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onUploadStart: PropTypes.func,
  onUploadEnd: PropTypes.func,
};

const DynamicFormRenderer = ({
  small,
  fieldData: {
    key,
    fieldType,
    fieldName,
    description,
    values,
    config,
    limit,
    charLimit,
    selectable,
  },
  resetKey,
  searchTypeKey = null,
  handleFormValues,
  formValues,
  minStartDate = null,
  timeZone,
  setTimeZone,
  autoSelectSingleRadio = false,
  hideIfSingleRadio = false,
  platformOptionOverrides = null,
  isBulkSearch = false,
  useCatalog = false,
  onUseCatalogChange = null,
  companyId = null,
  onUploadStart = null,
  onUploadEnd = null,
}) => {
  const isSingleRadio = fieldType === formInputTypes.radioSelect && values && values.length === 1;

  return (
    <ProductFormFieldContainer
      small={small}
      fieldTitle={fieldName}
      description={description}
      limit={limit}
      charLimit={charLimit}
      useClassName={(hideIfSingleRadio && isSingleRadio) ? 'display-hidden' : ''}
      inputConfig={config}
    >
      <FormComponentSelector
        searchTypeKey={searchTypeKey}
        resetKey={resetKey}
        inputKey={key}
        fieldType={fieldType}
        small={small}
        value={formValues[key]}
        options={values}
        inputConfig={config}
        handleFormValues={(val) => handleFormValues(val, key)}
        description={description}
        fieldTitle={fieldName}
        limit={limit}
        charLimit={charLimit}
        selectable={selectable}
        minStartDate={minStartDate}
        timeZone={timeZone}
        setTimeZone={setTimeZone}
        autoSelectSingleRadio={autoSelectSingleRadio}
        platformOptionOverrides={platformOptionOverrides}
        isBulkSearch={isBulkSearch}
        useCatalog={useCatalog}
        onUseCatalogChange={onUseCatalogChange}
        companyId={companyId}
        onUploadStart={onUploadStart}
        onUploadEnd={onUploadEnd}
      />
    </ProductFormFieldContainer>
  );
};

DynamicFormRenderer.propTypes = {
  fieldData: PropTypes.shape({
    key: PropTypes.string.isRequired,
    config: PropTypes.objectOf(PropTypes.any),
    fieldName: PropTypes.string.isRequired,
    fieldType: PropTypes.string.isRequired,
    description: PropTypes.string,
    selectable: PropTypes.bool,
    values: PropTypes.arrayOf(PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.bool,
    ]).isRequired),
    limit: PropTypes.number,
    charLimit: PropTypes.number,
  }).isRequired,
  resetKey: PropTypes.string.isRequired,
  searchTypeKey: PropTypes.string,
  minStartDate: PropTypes.instanceOf(Date),
  timeZone: PropTypes.string.isRequired,
  setTimeZone: PropTypes.func.isRequired,
  handleFormValues: PropTypes.func.isRequired,
  formValues: PropTypes.objectOf(PropTypes.any).isRequired,
  small: PropTypes.bool.isRequired,
  autoSelectSingleRadio: PropTypes.bool,
  hideIfSingleRadio: PropTypes.bool,
  platformOptionOverrides: PropTypes.arrayOf(PropTypes.string),
  isBulkSearch: PropTypes.bool,
  useCatalog: PropTypes.bool,
  onUseCatalogChange: PropTypes.func,
  companyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onUploadStart: PropTypes.func,
  onUploadEnd: PropTypes.func,
};

export default DynamicFormRenderer;
